import * as React from "react";
import { Card, CardBody, CardHeader, PageContainer } from "../components/Card";
import { PageHeader, SectionHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { ConfirmModal } from "../components/ConfirmModal";
import { Checkbox } from "../components/Form";
import { Table, Thead, Tbody, Tr, Th, Td } from "../components/Table";
import { Badge, StatusBadge } from "../components/Badge";
import { Banner, EmptyState, ListStates } from "../components/States";
import { IdBadge } from "../components/Detail";
import { useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { CalculationPreviewPageModel, CalculationPreviewRowModel, CalculationRunSummaryModel } from "../api/APIClient";
import { describeErrorWithFindings } from "../api/errors";
import { FindingList, FindingSummary } from "../procedures/FindingList";
import { ScopeBuilder } from "./ScopeBuilder";
import { CalculationScope, describeScopes } from "./scopes";
import * as Store from "../procedures/ProceduresStore";

/**
 * Running the calculation engine over a scope.
 *
 * This used to be a panel inside the 1,173-line procedure builder, reachable only while editing the
 * procedure that happened to own the assignment. It is its own screen now, because running a
 * calculation is a distinct act from authoring the rules that drive it - and often done by a
 * different person.
 *
 * The safe path is preview first, then run. Preview is exactly the same request with
 * `saveResults: false`, so what it shows is what the run will do - including which rows it refuses
 * to touch.
 *
 * `saveResults` and `overrideManualMarks` are required nullable bools server-side; omitting either
 * is a 400 rather than a default, so both are always sent explicitly.
 */

/**
 * Tailwind scans source for complete class names, so every colour used here has to appear as a
 * literal string. A constructed name like `text-${tone}-text` is never emitted into the stylesheet.
 */
const STAT_COLOURS = {
    success: "text-success-text",
    info: "text-info-text",
    danger: "text-danger-text",
    warning: "text-warning-text",
    neutral: "text-text-strong",
} as const;

type StatTone = keyof typeof STAT_COLOURS;

const ACTION_TONES: Record<string, "success" | "info" | "neutral" | "danger"> = {
    Create: "success",
    Update: "info",
    Unchanged: "neutral",
    Delete: "danger",
    Blocked: "danger",
};

export const CalculationsIndex: React.FC = () => {
    const toast = useToast();
    const canRun = usePermission(Permissions.CALCULATIONS_RUN);
    const canOverrideManual = usePermission(Permissions.CALCULATIONS_OVERRIDE_MANUAL);

    const [scopes, setScopes] = React.useState<CalculationScope[]>([{ kind: "QualificationModule", ids: [] }]);
    const [overrideManualMarks, setOverrideManualMarks] = React.useState(false);
    const [preview, setPreview] = React.useState<CalculationPreviewPageModel | null>(null);
    const [summary, setSummary] = React.useState<CalculationRunSummaryModel | null>(null);
    const [page, setPage] = React.useState(1);
    const [confirming, setConfirming] = React.useState(false);

    const pageSize = 50;

    /** A scope is only usable once it actually names something. */
    const scopeIsComplete = (scope: CalculationScope) =>
        scope.kind === "QualificationProgramLevelCycle"
            ? !!(scope.qualificationProgramLevelID && scope.assessmentCycleID)
            : scope.ids.length > 0;

    const usableScopes = scopes.filter(scopeIsComplete);
    const ready = usableScopes.length > 0;

    const loadPreview = async (targetPage: number) => {
        const result = await Store.previewCalculations(usableScopes, { saveResults: false, overrideManualMarks }, targetPage, pageSize);
        setPreview(result);
        setPage(targetPage);
        setSummary(null);
    };

    const runPreview = useAction(() => loadPreview(1), describeErrorWithFindings);
    const changePage = useAction(() => loadPreview(page), describeErrorWithFindings);

    const run = useAction(async () => {
        const result = await Store.runCalculations(usableScopes, { saveResults: true, overrideManualMarks });
        setSummary(result);
        setPreview(null);
        setConfirming(false);

        if (result.succeeded) {
            toast.success(
                `Calculated ${result.studentsProcessed ?? 0} student(s): ${result.createdResults ?? 0} created, ${
                    result.updatedResults ?? 0
                } updated.`
            );
        } else {
            toast.error("The run did not succeed. Check the findings below.");
        }
    }, describeErrorWithFindings);

    const rows = preview?.rows ?? [];
    const totalPages = preview ? Math.max(1, Math.ceil((preview.totalCount ?? 0) / (preview.pageSize ?? pageSize))) : 1;

    return (
        <PageContainer>
            <PageHeader
                title="Calculations"
                icon={["fad", "calculator"]}
                description="Run the engine over a scope. Preview first - it is the same request with saving turned off."
            />

            <Card className="mb-6">
                <CardHeader>
                    <SectionHeader title="Scope" description="What this run applies to." />
                </CardHeader>
                <CardBody>
                    <ScopeBuilder scopes={scopes} onChange={setScopes} />
                </CardBody>
            </Card>

            <Card className="mb-6">
                <CardHeader>
                    <SectionHeader title="Options" />
                </CardHeader>
                <CardBody>
                    <Checkbox
                        label="Overwrite manually entered marks"
                        hint={
                            canOverrideManual
                                ? "Off by default: a calculated value normally leaves a hand-entered mark alone."
                                : "You do not have permission to do this. The server decides regardless of what is sent here."
                        }
                        checked={overrideManualMarks}
                        disabled={!canOverrideManual}
                        onChange={(e) => setOverrideManualMarks(e.target.checked)}
                    />

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <Button
                            variant="outline-primary"
                            icon={["fad", "eye"]}
                            onClick={runPreview.run}
                            loading={runPreview.busy}
                            disabled={!ready}
                        >
                            Preview
                        </Button>
                        <Button
                            icon={["fad", "play"]}
                            onClick={() => setConfirming(true)}
                            disabled={!ready || !canRun}
                            title={canRun ? undefined : "Running a calculation needs the calculations.run permission."}
                        >
                            Calculate and save
                        </Button>

                        {ready && <span className="text-xs text-text-weak">Applies to {describeScopes(usableScopes)}.</span>}
                        {!ready && <span className="text-xs text-text-weak">Complete a scope above to enable these.</span>}
                    </div>

                    {runPreview.error && (
                        <Banner tone="danger" className="mt-3">
                            {runPreview.error}
                        </Banner>
                    )}
                    {run.error && (
                        <Banner tone="danger" className="mt-3">
                            {run.error}
                        </Banner>
                    )}
                </CardBody>
            </Card>

            {summary && <RunSummary summary={summary} />}

            {preview && (
                <Card>
                    <CardHeader>
                        <SectionHeader
                            title="Preview"
                            description={`${preview.totalCount ?? 0} row(s). Nothing has been saved.`}
                            actions={<FindingSummary findings={preview.findings} />}
                        />
                    </CardHeader>

                    <ListStates
                        isLoading={changePage.busy}
                        isEmpty={rows.length === 0}
                        empty={{
                            icon: ["fad", "calculator"],
                            title: "Nothing to calculate in this scope",
                            hint: "Either no assignment is active for it, or no enrolled students have the inputs the procedure needs.",
                        }}
                    >
                        <Table>
                            <Thead>
                                <Tr>
                                    <Th>Student</Th>
                                    <Th>Target</Th>
                                    <Th>Mark type</Th>
                                    <Th align="right">Current</Th>
                                    <Th align="right">Proposed</Th>
                                    <Th>Action</Th>
                                    <Th>Notes</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {rows.map((row, i) => (
                                    <PreviewRow key={`${row.studentID}-${row.targetEntityID}-${i}`} row={row} />
                                ))}
                            </Tbody>
                        </Table>
                    </ListStates>

                    {totalPages > 1 && (
                        <CardBody className="flex items-center justify-between border-t border-border">
                            <span className="text-xs text-text-weak">
                                Page {preview.page ?? page} of {totalPages}
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    icon={["fad", "chevron-left"]}
                                    disabled={(preview.page ?? page) <= 1 || changePage.busy}
                                    onClick={() => loadPreview((preview.page ?? page) - 1)}
                                >
                                    Previous
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    icon={["fad", "chevron-right"]}
                                    disabled={(preview.page ?? page) >= totalPages || changePage.busy}
                                    onClick={() => loadPreview((preview.page ?? page) + 1)}
                                >
                                    Next
                                </Button>
                            </div>
                        </CardBody>
                    )}

                    {(preview.findings ?? []).length > 0 && (
                        <CardBody className="border-t border-border">
                            <FindingList findings={preview.findings} />
                        </CardBody>
                    )}
                </Card>
            )}

            {!preview && !summary && (
                <Card>
                    <EmptyState
                        icon={["fad", "calculator"]}
                        title="Nothing run yet"
                        hint="Choose a scope and preview it. Preview writes nothing, so it is always safe."
                    />
                </Card>
            )}

            <ConfirmModal
                isOpen={confirming}
                onClose={() => setConfirming(false)}
                onConfirm={run.run}
                title="Calculate and save"
                icon={["fad", "play"]}
                confirmVariant="primary"
                confirmLabel="Calculate and save"
                busy={run.busy}
                error={run.error}
                body={
                    <>
                        <p>
                            This will calculate and <strong>save</strong> results for {describeScopes(usableScopes)}.
                        </p>
                        <p className="mt-2">
                            Finalized calculated results are preserved, and manually entered ones are left alone
                            {overrideManualMarks ? " unless you have permission to overwrite them, which you asked for above" : ""}.
                            Results this run no longer produces are removed.
                        </p>
                    </>
                }
            />
        </PageContainer>
    );
};

const PreviewRow: React.FC<{ row: CalculationPreviewRowModel }> = ({ row }) => {
    const action = row.action ?? "";
    const changed = action === "Create" || action === "Update";

    return (
        <Tr>
            <Td>
                {/* studentName is the id when the backend has no directory entry - show it as an id then. */}
                {row.studentName && row.studentName !== row.studentID ? (
                    <span className="font-medium text-text-strong">{row.studentName}</span>
                ) : (
                    <IdBadge id={row.studentID} />
                )}
            </Td>
            <Td>
                <span className="text-text">{row.targetName || row.targetEntityID}</span>
                <span className="ml-2 text-xs text-text-weak">{row.targetEntityType}</span>
            </Td>
            <Td>{row.markTypeName}</Td>
            <Td align="right">
                {row.currentResult === undefined || row.currentResult === null ? (
                    <span className="text-text-weak">&mdash;</span>
                ) : (
                    <span className="inline-flex items-center gap-1.5">
                        {row.currentResult} / {row.currentTotal}
                        <StatusBadge status={row.currentOrigin} />
                    </span>
                )}
            </Td>
            <Td align="right">
                {row.proposedResult === undefined || row.proposedResult === null ? (
                    <span className="text-text-weak">&mdash;</span>
                ) : (
                    <span className={changed ? "font-semibold text-text-strong" : "text-text"}>
                        {row.proposedResult} / {row.proposedTotal}
                    </span>
                )}
            </Td>
            <Td>
                <Badge tone={ACTION_TONES[action] ?? "neutral"}>{action || "—"}</Badge>
            </Td>
            <Td>
                {row.finalizationStatus === "Finalized" && <StatusBadge status="Finalized" />}
                {(row.findings ?? []).length > 0 && (
                    <span className="ml-1 text-xs text-warning-text">{(row.findings ?? []).length} finding(s)</span>
                )}
            </Td>
        </Tr>
    );
};

/**
 * The counters the backend returns after a run. Worth showing all of them: "skipped" is not an
 * error, it is the engine refusing to overwrite something it was told to protect, and that
 * distinction is exactly what people need to see.
 */
const RunSummary: React.FC<{ summary: CalculationRunSummaryModel }> = ({ summary }) => {
    const stats: Array<{ label: string; value?: number; tone?: StatTone }> = [
        { label: "Assignments processed", value: summary.assignmentsProcessed },
        { label: "Students processed", value: summary.studentsProcessed },
        { label: "Created", value: summary.createdResults, tone: "success" },
        { label: "Updated", value: summary.updatedResults, tone: "info" },
        { label: "Unchanged", value: summary.unchangedResults },
        { label: "Deleted", value: summary.deletedResults, tone: "danger" },
        { label: "Skipped (manual)", value: summary.skippedManualResults, tone: "warning" },
        { label: "Skipped (finalized)", value: summary.skippedFinalizedResults, tone: "warning" },
    ];

    return (
        <Card className="mb-6">
            <CardHeader>
                <SectionHeader
                    title="Run summary"
                    actions={
                        <div className="flex items-center gap-2">
                            <Badge tone={summary.succeeded ? "success" : "danger"}>{summary.succeeded ? "Succeeded" : "Failed"}</Badge>
                            <Badge tone={summary.saved ? "success" : "neutral"}>{summary.saved ? "Saved" : "Not saved"}</Badge>
                            <FindingSummary findings={summary.findings} />
                        </div>
                    }
                />
            </CardHeader>
            <CardBody>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {stats.map((stat) => (
                        <div key={stat.label} className="rounded-md border border-border bg-surface-muted p-3">
                            <div className="text-xs text-text-weak">{stat.label}</div>
                            <div className={`text-xl font-semibold ${STAT_COLOURS[stat.tone ?? "neutral"]}`}>
                                {stat.value ?? 0}
                            </div>
                        </div>
                    ))}
                </div>

                {((summary.skippedManualResults ?? 0) > 0 || (summary.skippedFinalizedResults ?? 0) > 0) && (
                    <Banner tone="info" className="mt-4">
                        Skipped rows are not failures. A manually entered mark is left alone unless overwriting is both requested and
                        permitted, and a finalized calculated result is preserved outright.
                    </Banner>
                )}

                {(summary.findings ?? []).length > 0 && (
                    <div className="mt-4">
                        <FindingList findings={summary.findings} />
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default CalculationsIndex;
